import { isClientView } from '../common/organizationAccessMode';
import React, { useEffect, useMemo, useState } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { serviceRequestDownloadUrl, uploadServiceRequestFile } from '../common/serviceRequestFiles';
import type { S01Document, S01Intake } from '../../functions/src/common/s01Workflow';
import { cleanS01MissingDocuments, incompleteS01DocumentSlots, S01_DOCUMENT_SLOTS, type S01DocumentCategory } from '../../functions/src/common/s01Documents';
import S01DocumentUploads from './S01DocumentUploads';

const S01ClientDocuments: React.FC<{ organizationId: string; requestId: string; onBack: () => void }> = ({ organizationId, requestId, onBack }) => {
    const callable = useMemo(() => httpsCallable(getFunctions(undefined, 'us-central1'), 'manageS01Assessment'), []);
    const [data, setData] = useState<{ reference: string; intake: S01Intake; documents: S01Document[];
        missingDocuments: S01DocumentCategory[]; locked: boolean; version: number } | null>(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');
    const load = async () => {
        try {
            const next = (await callable({ action: 'loadDocuments', organizationId, requestId })).data as any;
            setData({ ...next, locked: isClientView || next.locked, missingDocuments: Array.isArray(next.missingDocuments) ? cleanS01MissingDocuments(next.missingDocuments)
                : next.noDocuments ? S01_DOCUMENT_SLOTS.map(slot => slot.id) : [] });
        }
        catch (failure: any) { setError(failure.message || 'Documents failed loading.'); }
    };
    useEffect(() => { void load(); }, [organizationId, requestId]);
    const upload = async (files: FileList | null, category: S01DocumentCategory) => {
        if (!files?.length || !data)
            return;
        const selected = Array.from(files);
        if (selected.some(file => file.size < 1 || file.size > 25 * 1024 * 1024 ||
            !['application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'].includes(file.type))) {
            setError('Use PDF or DOCX files, under 25MB each.');
            return;
        }
        setBusy(true); setError(''); setMessage('');
        try {
            for (const file of selected) {
                const document = await uploadServiceRequestFile('attachment', organizationId, requestId, file);
                setData(current => current && ({ ...current, documents: [...current.documents, { ...document, category }],
                    missingDocuments: current.missingDocuments.filter(item => item !== category) }));
            }
        } catch (failure: any) { setError(failure.message || 'Document upload failed.'); }
        finally { setBusy(false); }
    };
    const save = async () => {
        if (!data)
            return;
        setBusy(true); setError(''); setMessage('');
        try {
            await callable({ action: 'saveDocuments', organizationId, requestId,
                documents: data.documents, missingDocuments: data.missingDocuments, version: data.version });
            await load();
            setMessage('Documents saved. Your assessment will use the updated information.');
        } catch (failure: any) { setError(failure.message || 'Documents failed saving.'); }
        finally { setBusy(false); }
    };
    const download = async (document: S01Document) => {
        try { window.open(await serviceRequestDownloadUrl(organizationId, requestId, document), '_blank', 'noopener,noreferrer'); }
        catch (failure: any) { setError(failure.message || 'Document failed opening.'); }
    };
    return <div className="mx-auto max-w-4xl space-y-5 p-6 text-slate-900">
        <button onClick={onBack} className="font-bold text-blue-700">← Back</button>
        <h1 className="text-2xl font-black text-slate-900">FSAT Standard Documents</h1>
        {error && <p role="alert" className="text-sm font-semibold text-red-700">{error}</p>}
        {message && <p role="status" className="text-sm font-semibold text-emerald-700">{message}</p>}
        {!data ? <p>Loading documents…</p> : <>
            <p className="text-sm text-slate-600">{data.reference} · {data.intake.facilityName}</p>
            <S01DocumentUploads documents={data.documents} missingDocuments={data.missingDocuments} disabled={busy || data.locked}
                onUpload={(files, category) => void upload(files, category)}
                onMissingChange={(category, missing) => setData({ ...data, missingDocuments: missing
                    ? [...data.missingDocuments, category] : data.missingDocuments.filter(item => item !== category) })}
                onRemove={path => setData({ ...data, documents: data.documents.filter(document => document.path !== path) })}
                onCategorize={(path, category) => setData({ ...data, documents: data.documents.map(document => document.path === path ? { ...document, category } : document),
                    missingDocuments: data.missingDocuments.filter(item => item !== category) })} />
            {!data.locked && <button disabled={busy || !!incompleteS01DocumentSlots(data.documents, data.missingDocuments).length} onClick={() => void save()} className="rounded-lg bg-blue-700 px-5 py-2 font-bold text-white disabled:opacity-50">{busy ? 'Working…' : 'Save Documents'}</button>}
            <div className="flex flex-wrap gap-3">{data.documents.map(document => <button key={document.path} onClick={() => void download(document)} className="text-sm font-bold text-blue-700">Open {document.name}</button>)}</div>
            {data.locked && <p className="text-sm text-slate-500">{isClientView ? 'Documents are read-only in client view.' : 'This request is closed. Your delivered report remains available in request details.'}</p>}
        </>}
    </div>;
};

export default S01ClientDocuments;
